import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ConsentType, InsignificantChangeNoticeResponseDto } from '@markant-platform/consent/data-access';
import { CmsLink, InternalCmsLink } from '@markant-platform/shared/content/model';
import { handleDownloadByObjectUrl } from '@markant-platform/shared/util-helpers';

@Component({
  selector: 'markant-platform-consent-dialog',
  templateUrl: './consent-dialog.component.html',
  styleUrls: ['./consent-dialog.component.scss'],
})
export class ConsentDialogComponent implements OnInit {
  @Input() open = false;
  @Input() consentChanges: (InsignificantChangeNoticeResponseDto & { url?: string })[] = [];
  @Input() activeLanguage = '';
  @Input() termsOfUseLink$: Observable<InternalCmsLink> = of();
  @Input() privacyPolicyLink$: Observable<InternalCmsLink> = of();
  @Input() serviceGtcLink$: Observable<CmsLink> = of();

  @Output() openChange = new EventEmitter<boolean>();

  hasPrivacyPolicyChanges = false;
  hasTermsOfUseChanges = false;
  hasServiceConditionsChanges = false;
  hasServiceGtcChanges = false;
  hasMultipleServiceConditionsChanged = false;

  ngOnInit(): void {
    this.hasPrivacyPolicyChanges = this.consentChanges.some(
      element => element.consentType === ConsentType.PLATFORM_PRIVACY_NOTICE
    );
    this.hasTermsOfUseChanges = this.consentChanges.some(
      element => element.consentType === ConsentType.PLATFORM_TERMS_OF_USE
    );
    this.hasServiceGtcChanges = this.consentChanges.some(element => element.consentType === ConsentType.SERVICE_GTC);
    this.hasServiceConditionsChanges = this.consentChanges.some(
      element => element.consentType === ConsentType.SERVICE_CONDITIONS
    );

    if (this.hasServiceConditionsChanges) {
      this.hasMultipleServiceConditionsChanged =
        this.consentChanges.filter(element => element.consentType === ConsentType.SERVICE_CONDITIONS).length > 1;
    }
  }

  closeDialog(): void {
    this.open = false;
    this.openChange.emit(false);
  }

  getPrivacyPolicyChanges(): string[] {
    const found = this.consentChanges.filter(element => element.consentType === ConsentType.PLATFORM_PRIVACY_NOTICE);
    return this.getChangesFromConsents(found).map(changes => changes.description);
  }

  getTermsOfUseChanges(): string[] {
    const found = this.consentChanges.filter(element => element.consentType === ConsentType.PLATFORM_TERMS_OF_USE);
    return this.getChangesFromConsents(found).map(changes => changes.description);
  }

  getServiceGtcChanges(): string[] {
    const found = this.consentChanges.filter(element => element.consentType === ConsentType.SERVICE_GTC);
    return this.getChangesFromConsents(found).map(changes => changes.description);
  }

  getServiceConditionsChanges(): { description: string; name?: string; url?: string }[] {
    const found = this.consentChanges.filter(element => element.consentType === ConsentType.SERVICE_CONDITIONS);
    return this.getChangesFromConsents(found);
  }

  downloadServiceDocument(url?: string): void {
    if (url) {
      handleDownloadByObjectUrl(url);
    }
  }

  openServiceGTCs(serviceGtcLink: CmsLink): void {
    if (serviceGtcLink) {
      window.open(serviceGtcLink.href, serviceGtcLink.target);
    }
  }

  private getChangesFromConsents(consents: (InsignificantChangeNoticeResponseDto & { url?: string })[]): {
    description: string;
    name?: string;
    url?: string;
  }[] {
    if (consents.length < 1) {
      return [];
    }

    const changes: { description: string; name?: string; url?: string }[] = [];
    consents.forEach(consentChangeNotice => {
      changes.push({
        description: consentChangeNotice.changeDescription,
        name: consentChangeNotice.cartridgeName,
        url: consentChangeNotice.url,
      });
    });

    return changes;
  }
}
